/**
 * aud2 Д-5 — причина удаления спрашивается ТАМ, ГДЕ СЕРВЕР ЕЁ ПОПРОСИЛ.
 *
 * Ядро отменило смягчение гейта причины: удаление без причины больше не
 * записывается отметкой «причина не указана», а отказывает (422 с полем `reason`).
 * Это правильно — прежняя отметка превращала гейт в его вид. Но у панели есть
 * тридцать девять маршрутов удаления, и дописывать поле причины в каждый экран
 * значило бы тридцать девять правок и тридцать девять способов её не дописать.
 *
 * Поэтому вопрос живёт в ОДНОЙ точке и задаётся не заранее, а по отказу: сервер
 * сам называет разделы, где причина обязательна, и только там человек её видит.
 * Ни одного лишнего вопроса на удалениях, где причина не нужна.
 */

/** Отказ ядра «нужна причина» — распознаётся по полю ошибки, а не по тексту. */
export function needsDeleteReason(status: number | undefined, data: unknown): boolean {
  if (status !== 422 || data === null || typeof data !== 'object') {
    return false;
  }

  const errors = (data as { errors?: unknown }).errors;

  if (errors === null || typeof errors !== 'object') {
    return false;
  }

  const reason = (errors as Record<string, unknown>).reason;

  return Array.isArray(reason) && reason.length > 0;
}

/** Текст отказа — его пишет ядро и оно же называет раздел. */
export function reasonPrompt(data: unknown): string {
  if (data !== null && typeof data === 'object') {
    const errors = (data as { errors?: Record<string, unknown> }).errors;
    const reason = errors?.reason;

    if (Array.isArray(reason) && typeof reason[0] === 'string') {
      return reason[0];
    }

    const message = (data as { message?: unknown }).message;

    if (typeof message === 'string') {
      return message;
    }
  }

  return 'Удаление без причины не выполняется.';
}

/** Минимальная длина причины — та же, что у ядра [TZ-R2 §4.4]. */
export const REASON_MIN = 3;

/**
 * Спросить причину. Возвращает строку либо null, если человек передумал —
 * тогда удаление просто не происходит, и это законный исход.
 *
 * Диалог собирается вручную, а не компонентом: он вызывается из перехватчика
 * axios, у которого нет ни дерева React, ни маршрута под рукой.
 */
/**
 * ⛔⛔ ЗАГОЛОВОК ВОПРОСА ГОВОРИТ О ТОМ, ЧТО ЧЕЛОВЕК ДЕЛАЛ (`s322`, Д15).
 *
 * Перехватчик один на все отказы «нужна причина», а заголовок у диалога стоял
 * ОДИН — «Почему удаляем?». Владелец потянул за край свежую запись, получил
 * отказ правки прошлого — и прочитал, что у него что-то удаляют: «я попытался
 * растянуть созданную запись, получил вагон ошибок».
 *
 * Род действия знает сам запрос: `DELETE` — удаление, всё остальное — правка.
 * Придумывать роду имя больше нечем: перехватчик живёт вне дерева React и до
 * словаря экрана не дотягивается, поэтому слов ровно два и они общие.
 */
export function deleteReasonTitle(method: string | undefined): string {
  return (method ?? '').toLowerCase() === 'delete' ? 'Почему удаляем?' : 'Почему меняем?';
}

export function askDeleteReason(prompt: string, zagolovok = 'Почему удаляем?'): Promise<string | null> {
  if (typeof document === 'undefined') {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className =
      'fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4';

    const card = document.createElement('div');
    card.className =
      'w-full max-w-md rounded-lg bg-white p-5 shadow-xl dark:bg-neutral-900 dark:text-neutral-100';

    const title = document.createElement('h2');
    title.className = 'mb-2 text-base font-semibold';
    title.textContent = zagolovok;

    const text = document.createElement('p');
    text.className = 'mb-3 text-sm text-neutral-600 dark:text-neutral-400';
    text.textContent = prompt;

    const input = document.createElement('input');
    input.type = 'text';
    input.placeholder = 'Например: дубль карточки';
    input.className =
      'mb-3 w-full rounded-md border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-500 dark:border-neutral-700 dark:bg-neutral-800';

    const hint = document.createElement('p');
    hint.className = 'mb-4 text-xs text-neutral-500';
    hint.textContent = `Не меньше ${REASON_MIN} символов. Причина попадёт в след и останется навсегда.`;

    const row = document.createElement('div');
    row.className = 'flex justify-end gap-2';

    // ⛔ КНОПКИ НАЗЫВАЮТ ТО ЖЕ ДЕЙСТВИЕ, ЧТО И ЗАГОЛОВОК (`s322`, Д15): «Удалить»
    // под вопросом «Почему меняем?» — это обещание не того хода.
    const udalenie = zagolovok === 'Почему удаляем?';

    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.className =
      'rounded-md px-3 py-2 text-sm text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800';
    cancel.textContent = udalenie ? 'Не удалять' : 'Не менять';

    const submit = document.createElement('button');
    submit.type = 'button';
    submit.className =
      'rounded-md bg-neutral-900 px-3 py-2 text-sm font-medium text-white disabled:opacity-40 dark:bg-neutral-100 dark:text-neutral-900';
    submit.textContent = udalenie ? 'Удалить' : 'Сохранить';
    submit.disabled = true;

    const close = (value: string | null) => {
      document.removeEventListener('keydown', onKey);
      overlay.remove();
      resolve(value);
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close(null);
      }
      if (e.key === 'Enter' && !submit.disabled) {
        close(input.value.trim());
      }
    };

    input.addEventListener('input', () => {
      submit.disabled = input.value.trim().length < REASON_MIN;
    });
    cancel.addEventListener('click', () => close(null));
    submit.addEventListener('click', () => close(input.value.trim()));
    overlay.addEventListener('mousedown', (e) => {
      if (e.target === overlay) {
        close(null);
      }
    });
    document.addEventListener('keydown', onKey);

    row.append(cancel, submit);
    card.append(title, text, input, hint, row);
    overlay.append(card);
    document.body.append(overlay);
    input.focus();
  });
}

/**
 * Дописать причину в тело запроса, не потеряв того, что там уже было.
 * Тело axios бывает строкой (JSON), объектом или пустым — все три случая живые.
 * Поле по умолчанию — `reason`; замок зарплаты (`s966`) просит `zamok_prichina`.
 */
export function withReason(data: unknown, reason: string, pole = 'reason'): unknown {
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);

      return JSON.stringify({ ...(parsed as object), [pole]: reason });
    } catch {
      return JSON.stringify({ [pole]: reason });
    }
  }

  if (data !== null && typeof data === 'object' && !(data instanceof FormData)) {
    return { ...(data as object), [pole]: reason };
  }

  if (data instanceof FormData) {
    data.append(pole, reason);

    return data;
  }

  return { [pole]: reason };
}
